/**
 * The committed-route publisher for page context v2 (COM-585 §3).
 *
 * Owns the three things the contract needs held together for one document:
 * the navigation-id sequence, the last-published navigation key (the C3 dedupe
 * axis), and the previous route's URL. Kept out of `root.tsx` because that file
 * cannot be imported under vitest — the same constraint that produced
 * `page-type-selection.ts`.
 *
 * DEPENDENCY-INJECTED, not `window`-coupled: the sink is a constructor
 * argument, so the publish rules are testable against a plain array. The React
 * wiring in `root.tsx` supplies the real dataLayer sink.
 *
 * Design: docs/page-context-v2-design.md.
 */
import {
  INITIAL_NAVIGATION_ORDINAL,
  PAGE_CONTEXT_READY_EVENT,
  type PageContext,
  buildPageContext,
  createNavigationIdFactory,
  navigationKey,
  shouldPublishContext,
  toPageContextEvent,
} from "./page-context";
import { type PageTitleSiteDefaults, resolvePageTitle } from "./page-title";

/** What a committed navigation hands the publisher. */
export interface CommittedNavigation {
  pathname: string;
  search: string;
  /** Absolute URL of the committed route, query-value casing preserved. */
  href: string;
  /** The committed route's already-resolved `seo` payload (may be a promise). */
  seo: unknown;
  pageType: string | undefined;
}

export interface PageContextPublisherOptions {
  documentId: string;
  siteDefaults: PageTitleSiteDefaults;
  /** Receives each published context message. */
  sink: (event: ReturnType<typeof toPageContextEvent>) => void;
  /** Called with the freshly published context, for the `window` singleton. */
  onPublish?: (context: PageContext) => void;
  /**
   * The key the SSR initial context already published, so hydration does not
   * immediately republish the landing route. Mirrors how `PageTypeTracker`
   * seeds `lastPushed` with the SSR-pushed value.
   */
  initialKey: string | null;
  /** The landing route's URL, so the FIRST soft nav reports a previous route. */
  initialHref: string | null;
}

export interface PageContextPublisher {
  /**
   * Publish if this commit is a meaningful navigation. Returns whether it
   * published — the return value is what the 25-navigation count asserts on.
   */
  publish(navigation: CommittedNavigation): boolean;
}

export function createPageContextPublisher({
  documentId,
  siteDefaults,
  sink,
  onPublish,
  initialKey,
  initialHref,
}: PageContextPublisherOptions): PageContextPublisher {
  // The SSR context consumed ordinal 0, so client navigations continue from 1
  // and the document's ids form one unbroken sequence.
  const nextNavigationId = createNavigationIdFactory(
    documentId,
    INITIAL_NAVIGATION_ORDINAL + 1,
  );
  let lastKey = initialKey;
  let previousHref = initialHref;

  return {
    publish(navigation) {
      const key = navigationKey(navigation.pathname, navigation.search);
      // Intermediate router notifications during a navigation still carry the
      // PREVIOUS location, so they produce the previous key and fall out here —
      // which is how "one publish per committed navigation" holds without an
      // explicit idle check. A genuine A→B→A return changes the key and
      // publishes, as C3 requires.
      if (!shouldPublishContext(lastKey, key)) return false;
      lastKey = key;

      const context = buildPageContext({
        navigationId: nextNavigationId(),
        pageLocation: navigation.href,
        pageTitle: resolvePageTitle(navigation.seo, siteDefaults),
        pageType: navigation.pageType,
        previousPageLocation: previousHref,
      });
      previousHref = navigation.href;

      // Context FIRST, then any route-bound event stamped from it (C4). The
      // sink is synchronous for exactly this reason — deferring the push would
      // let an effect-driven event land ahead of its own context.
      sink(toPageContextEvent(PAGE_CONTEXT_READY_EVENT, context));
      onPublish?.(context);
      return true;
    },
  };
}
